import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/lib/adminAuth";
import { Lock, Mail, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
  const { login } = useAdminAuth();
  const nav = useNavigate();
  const [identifier, setId] = useState("");
  const [password, setPw] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier || !password || !code) { toast.error("All three fields are required"); return; }
    setBusy(true);
    try {
      await login(identifier.trim(), password, code.trim());
      toast.success("Welcome back");
      nav("/back-office", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FDFBF7]">
      {/* Left brand pane */}
      <div className="hidden md:flex flex-col justify-between p-14 text-[#FDFBF7] relative overflow-hidden" style={{ background: "#0A4C36" }}>
        <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute right-40 top-24 w-56 h-56 rounded-full bg-white/5" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] uppercase tracking-widest">
            <ShieldCheck size={12} /> Restricted area
          </div>
          <h1 className="mt-8 text-4xl md:text-5xl font-semibold leading-tight" style={{ fontFamily: "Instrument Sans" }}>
            HealthCircles<br/>Back Office
          </h1>
          <p className="mt-4 text-sm opacity-80 max-w-sm">
            Operator console for approvals, doctor & patient management, plans, and platform health.
          </p>
        </div>
        <div className="relative text-xs opacity-60">v 1.0.0 · Not linked from public site</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-16">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="admin-login-form">
          <div className="text-[11px] uppercase tracking-widest text-[#8A9992]">Sign in</div>
          <h2 className="mt-2 text-3xl font-semibold text-[#1F2A24]" style={{ fontFamily: "Instrument Sans" }}>Operator access</h2>
          <p className="mt-2 text-sm text-[#6C7B72]">Enter the admin credentials <b>and</b> the shared access code.</p>

          <div className="mt-8 space-y-4">
            <Field icon={<Mail size={14} />} label="Admin identifier">
              <input
                data-testid="admin-login-identifier"
                autoComplete="username"
                value={identifier}
                onChange={(e) => setId(e.target.value)}
                placeholder="admin@health$circles"
                className="input-clean pl-10"
              />
            </Field>
            <Field icon={<Lock size={14} />} label="Password">
              <input
                data-testid="admin-login-password"
                autoComplete="current-password"
                type="password"
                value={password}
                onChange={(e) => setPw(e.target.value)}
                className="input-clean pl-10"
              />
            </Field>
            <Field icon={<KeyRound size={14} />} label="Access code">
              <input
                data-testid="admin-login-code"
                autoComplete="one-time-code"
                type="password"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="input-clean pl-10"
                placeholder="Shared operator code"
              />
            </Field>
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="mt-8 btn-emerald w-full justify-center py-3"
          >
            {busy ? "Signing in…" : "Sign in →"}
          </button>

          <div className="mt-8 text-[11px] text-[#8A9992] text-center">
            Unauthorised access is prohibited. All actions are logged.
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ icon, label, children }) {
  return (
    <label className="block">
      <div className="field-label mb-2">{label}</div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A9992]">{icon}</span>
        {children}
      </div>
    </label>
  );
}
